import { useMemo, useState } from 'react'
import { ChevronRight, KeyRound, LogOut, Settings, Shield, User2 } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/Modal'
import { loadSession, setFlashToast } from '../../../auth/state'
import { startLogout } from '../../../auth/oidc'

function pickUser(session) {
  const u = session?.user && typeof session.user === 'object' ? session.user : null
  const email = String(u?.email || '').trim()
  const name = String(u?.name || u?.preferred_username || '').trim()
  const picture = String(u?.picture || '').trim()
  return { email, name: name || (email ? email.split('@')[0] : ''), picture }
}

function Item({ icon: Icon, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-3xl border-2 border-brand-textDark/10 dark:border-slate-800 bg-white dark:bg-slate-950 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-2xl border-2 border-brand-textDark bg-brand-yellow flex items-center justify-center shadow-[2px_2px_0px_0px_#E5BC2E]">
          <Icon className="w-5 h-5" strokeWidth={2.6} />
        </div>
        <div className="min-w-0 text-left">
          <div className="font-black truncate">{title}</div>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-brand-text/60" strokeWidth={3} />
    </button>
  )
}

export default function AppPreviewMine() {
  const session = useMemo(() => loadSession(), [])
  const user = useMemo(() => pickUser(session), [session])
  const authed = Boolean(session?.tokens?.accessToken)

  const [accountOpen, setAccountOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [privacyOpen, setPrivacyOpen] = useState(false)

  const doLogout = () => {
    setFlashToast({ kind: 'success', title: 'Signed out', message: 'See you next time.' })
    startLogout()
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-3xl border-2 border-brand-textDark bg-white overflow-hidden flex items-center justify-center">
            {user.picture ? (
              <img src={user.picture} alt="me" className="w-full h-full object-cover" />
            ) : (
              <User2 className="w-7 h-7 text-brand-textDark/70" strokeWidth={2.6} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-lg font-black truncate">{authed ? user.name || 'User' : 'Guest'}</div>
            <div className="mt-1 text-sm font-bold text-brand-text/70 truncate">{authed ? user.email || '—' : 'Not signed in'}</div>
          </div>
          {!authed ? (
            <Button as="a" href="/login?next=/app/mine" variant="secondary" size="sm">
              Sign in
            </Button>
          ) : null}
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">My account</div>
        <Item icon={User2} title="My account" onClick={() => setAccountOpen(true)} />
        <Item icon={KeyRound} title="Passwords" onClick={() => setPasswordOpen(true)} />
      </Card>

      <Card className="p-5 space-y-3">
        <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">Settings</div>
        <Item icon={Settings} title="Settings" onClick={() => setSettingsOpen(true)} />
      </Card>

      <Card className="p-5 space-y-3">
        <div className="text-xs font-black tracking-widest uppercase text-brand-text/60">About</div>
        <Item icon={Shield} title="Privacy" onClick={() => setPrivacyOpen(true)} />
        <Item icon={ChevronRight} title="About" onClick={() => setAboutOpen(true)} />
      </Card>

      {authed ? (
        <Card className="p-5">
          <Button variant="neutral" size="lg" className="w-full justify-center" onClick={doLogout}>
            <LogOut className="w-5 h-5" />
            Sign out
          </Button>
        </Card>
      ) : null}

      <Modal
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        title="My account"
        footer={<Button variant="secondary" onClick={() => setAccountOpen(false)}>OK</Button>}
      >
        <div className="space-y-2 text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          <div>
            <span className="text-brand-text/70">Name：</span>
            <span className="font-black">{user.name || '—'}</span>
          </div>
          <div>
            <span className="text-brand-text/70">Email：</span>
            <span className="font-black">{user.email || '—'}</span>
          </div>
          <div className="text-brand-text/70">
            账户信息来自 IAM / Apple / Google 登录的 ID Token。
          </div>
        </div>
      </Modal>

      <Modal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title="Passwords"
        footer={<Button variant="secondary" onClick={() => setPasswordOpen(false)}>OK</Button>}
      >
        <div className="space-y-3 text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          <div>密码管理由 IAM 提供（推荐在系统登录页修改）。</div>
          <Button as="a" href="/login?fresh=1&next=/app/mine" variant="secondary">
            Open sign-in portal
          </Button>
        </div>
      </Modal>

      <Modal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Settings"
        footer={<Button variant="secondary" onClick={() => setSettingsOpen(false)}>OK</Button>}
      >
        <div className="text-sm font-bold text-brand-textDark/80 dark:text-slate-200">这里放应用设置（后续接入）。</div>
      </Modal>

      <Modal
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        title="Privacy"
        footer={<Button variant="secondary" onClick={() => setPrivacyOpen(false)}>OK</Button>}
      >
        <div className="text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          隐私说明（占位）：上传内容默认仅自己可见；设置为 Public 后将进入公共库。
        </div>
      </Modal>

      <Modal
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        title="About"
        footer={<Button variant="secondary" onClick={() => setAboutOpen(false)}>OK</Button>}
      >
        <div className="space-y-2 text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          <div>Anipin Web Preview</div>
          <div className="text-brand-text/70">用于快速验收 UI/交互；后续会替换为原生 App。</div>
        </div>
      </Modal>
    </div>
  )
}
